/**
 * Pure helpers for moving V2 screen variable dependencies through canvas
 * copy/paste. The service intentionally has no Firebase or React imports so it
 * can be covered with focused unit tests and reused by the canvas command
 * pipeline.
 */

import type { FunnelVariable, StepNode } from '@segmently/schema-models/v2';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import { getVariableReference, slugifyVariableRef } from '@modules/v2-variable-utils';
import { remapVariableReferencesInSteps } from '@modules/v2-variable-utils/services/VariableReferenceRemap';

type AnyRecord = Record<string, unknown>;
type VariableWithBindings = FunnelVariable & {
  boundScreenIds?: string[];
  screenBindings?: Record<string, Record<string, string>>;
  // screenScoreEffects comes from FunnelVariable itself (Record<string, ScoreEffect[]>).
  boundSectionId?: string;
  scope?: 'screen' | 'global';
};

export interface V2ClipboardVariablePayload {
  variables: Record<string, FunnelVariable>;
  variableDependencyIds: string[];
  warnings?: string[];
}

export interface V2ClipboardVariableSource {
  projectId?: string;
  onboardingId?: string;
  versionId?: string;
}

export interface V2BuildClipboardVariablePayloadInput {
  steps: FlowStepNode[];
  selectedStepIds: string[];
  variables: Record<string, FunnelVariable>;
  source?: V2ClipboardVariableSource;
}

export interface V2PasteVariablePlanInput {
  clipboardVariables?: Record<string, FunnelVariable>;
  originalStepIds: string[];
  stepIdMapping: Map<string, string>;
  targetVariables: Record<string, FunnelVariable>;
}

export interface V2PasteVariablePlan {
  variablesToCreate: FunnelVariable[];
  variableIdMapping: Map<string, string>;
  variableRefMapping: Map<string, string>;
  createdVariableIds: string[];
  warnings: string[];
}

export interface V2StepVariableRewriteInput {
  steps: FlowStepNode[];
  variableIdMapping: Map<string, string>;
  variableRefMapping: Map<string, string>;
}

const TEMPLATE_REF_PATTERN = /@([A-Za-z0-9_-]+)((?:\.[A-Za-z0-9_-]+)*)/g;

function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function isRecord(value: unknown): value is AnyRecord {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function normalizeName(value: unknown): string {
  return String(value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function sortedUnique(values: Iterable<string>): string[] {
  return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b));
}

function walkObject(node: unknown, visitor: (obj: AnyRecord) => void): void {
  if (Array.isArray(node)) {
    for (const item of node) {
      walkObject(item, visitor);
    }
    return;
  }

  if (!isRecord(node)) return;

  visitor(node);

  for (const value of Object.values(node)) {
    walkObject(value, visitor);
  }
}

function buildVariableReferenceIndex(
  variables: Record<string, FunnelVariable>,
): Map<string, string> {
  const index = new Map<string, string>();

  for (const variable of Object.values(variables)) {
    if (!variable?.id) continue;
    index.set(variable.id, variable.id);
    index.set(getVariableReference(variable), variable.id);
    if (variable.name) {
      index.set(slugifyVariableRef(variable.name), variable.id);
    }
  }

  return index;
}

function addResolvedVariableReference(
  rawRef: string,
  variables: Record<string, FunnelVariable>,
  refIndex: Map<string, string>,
  dependencyIds: Set<string>,
  missingRefs: Set<string>,
): void {
  const variableId = variables[rawRef] ? rawRef : refIndex.get(rawRef);
  if (variableId && variables[variableId]) {
    dependencyIds.add(variableId);
    return;
  }
  missingRefs.add(rawRef);
}

function collectTemplateReferences(
  text: string,
  variables: Record<string, FunnelVariable>,
  refIndex: Map<string, string>,
  dependencyIds: Set<string>,
  missingRefs: Set<string>,
): void {
  for (const match of text.matchAll(TEMPLATE_REF_PATTERN)) {
    addResolvedVariableReference(match[1], variables, refIndex, dependencyIds, missingRefs);
  }
}

function collectStepReferences(
  step: FlowStepNode,
  selectedStepIds: Set<string>,
  variables: Record<string, FunnelVariable>,
  refIndex: Map<string, string>,
  dependencyIds: Set<string>,
  missingRefs: Set<string>,
): void {
  walkObject(step, (obj) => {
    if (Array.isArray(obj.edges)) {
      for (const edge of obj.edges) {
        if (!isRecord(edge)) continue;
        const targetId = typeof edge.nextScreenId === 'string' ? edge.nextScreenId : '';
        if (!selectedStepIds.has(targetId)) continue;

        const conditions = Array.isArray(edge.conditions) ? edge.conditions : [];
        for (const condition of conditions) {
          if (!isRecord(condition) || typeof condition.key !== 'string') continue;
          addResolvedVariableReference(
            condition.key,
            variables,
            refIndex,
            dependencyIds,
            missingRefs,
          );
        }
      }
    }

    for (const [key, value] of Object.entries(obj)) {
      if (
        typeof value === 'string' &&
        (key === 'variableId' || key === 'targetVariableId' || key === 'emailVariableId')
      ) {
        addResolvedVariableReference(value, variables, refIndex, dependencyIds, missingRefs);
      }

      if (typeof value === 'string' && value.includes('@')) {
        collectTemplateReferences(value, variables, refIndex, dependencyIds, missingRefs);
      }
    }
  });
}

function collectTransitiveVariableDependencies(
  initialIds: Set<string>,
  variables: Record<string, FunnelVariable>,
  selectedStepIds: Set<string>,
  missingRefs: Set<string>,
): Set<string> {
  const dependencyIds = new Set(initialIds);
  const queue = Array.from(initialIds);

  while (queue.length > 0) {
    const variableId = queue.shift()!;
    const variable = variables[variableId] as VariableWithBindings | undefined;
    if (!variable?.screenScoreEffects) continue;

    for (const [screenId, effects] of Object.entries(variable.screenScoreEffects)) {
      if (!selectedStepIds.has(screenId) || !Array.isArray(effects)) continue;
      for (const effect of effects) {
        const targetVariableId = effect?.targetVariableId;
        if (typeof targetVariableId !== 'string' || !targetVariableId) continue;
        if (!variables[targetVariableId]) {
          missingRefs.add(targetVariableId);
          continue;
        }
        if (!dependencyIds.has(targetVariableId)) {
          dependencyIds.add(targetVariableId);
          queue.push(targetVariableId);
        }
      }
    }
  }

  return dependencyIds;
}

function formatMissingRefWarning(missingRefs: Set<string>): string[] {
  if (missingRefs.size === 0) return [];
  return [
    `Clipboard contains unresolved variable reference(s): ${sortedUnique(missingRefs).join(', ')}`,
  ];
}

function nextForkSuffix(baseId: string, usedIds: Set<string>, prefixIndex: number): string {
  return prefixIndex === 1 ? `copy_${baseId}` : `copy_${prefixIndex}_${baseId}`;
}

function nextForkName(baseName: string, usedNames: Set<string>, prefixIndex: number): string {
  const label = prefixIndex === 1 ? `Copy: ${baseName}` : `Copy ${prefixIndex}: ${baseName}`;
  return usedNames.has(normalizeName(label))
    ? nextForkName(baseName, usedNames, prefixIndex + 1)
    : label;
}

function makeForkedIdentity(
  source: FunnelVariable,
  usedIds: Set<string>,
  usedNames: Set<string>,
): { id: string; name: string } {
  const baseName = source.name || source.id || 'Variable';
  let prefixIndex = 1;
  let id = nextForkSuffix(source.id, usedIds, prefixIndex);

  while (usedIds.has(id)) {
    prefixIndex += 1;
    id = nextForkSuffix(source.id, usedIds, prefixIndex);
  }

  const name = nextForkName(baseName, usedNames, prefixIndex);
  return { id, name };
}

function remapVariableScreenFields(
  variable: VariableWithBindings,
  stepIdMapping: Map<string, string>,
  variableIdMapping: Map<string, string>,
): VariableWithBindings {
  const remapped = deepClone(variable) as VariableWithBindings;

  if (Array.isArray(variable.boundScreenIds)) {
    remapped.boundScreenIds = variable.boundScreenIds
      .map(screenId => stepIdMapping.get(screenId))
      .filter((screenId): screenId is string => Boolean(screenId));
  }

  if (variable.screenBindings) {
    const nextBindings: Record<string, Record<string, string>> = {};
    for (const [screenId, bindings] of Object.entries(variable.screenBindings)) {
      const nextScreenId = stepIdMapping.get(screenId);
      if (!nextScreenId) continue;
      nextBindings[nextScreenId] = deepClone(bindings);
    }
    remapped.screenBindings = nextBindings;
  }

  if (variable.screenScoreEffects) {
    const nextEffects: NonNullable<typeof variable.screenScoreEffects> = {};
    for (const [screenId, effects] of Object.entries(variable.screenScoreEffects)) {
      const nextScreenId = stepIdMapping.get(screenId);
      if (!nextScreenId || !Array.isArray(effects)) continue;
      nextEffects[nextScreenId] = effects.map((effect) => {
        const clonedEffect = deepClone(effect);
        const targetVariableId = clonedEffect.targetVariableId;
        if (typeof targetVariableId === 'string' && variableIdMapping.has(targetVariableId)) {
          clonedEffect.targetVariableId = variableIdMapping.get(targetVariableId)!;
        }
        return clonedEffect;
      });
    }
    remapped.screenScoreEffects = nextEffects;
  }

  return remapped;
}

export class V2ClipboardVariablePortabilityService {
  buildClipboardPayload(
    input: V2BuildClipboardVariablePayloadInput,
  ): V2ClipboardVariablePayload {
    const selectedStepIds = new Set(input.selectedStepIds);
    const dependencyIds = new Set<string>();
    const missingRefs = new Set<string>();
    const refIndex = buildVariableReferenceIndex(input.variables);

    for (const variable of Object.values(input.variables)) {
      const boundScreenIds = (variable as VariableWithBindings).boundScreenIds || [];
      if (boundScreenIds.some(screenId => selectedStepIds.has(screenId))) {
        dependencyIds.add(variable.id);
      }
    }

    for (const step of input.steps) {
      collectStepReferences(
        step,
        selectedStepIds,
        input.variables,
        refIndex,
        dependencyIds,
        missingRefs,
      );
    }

    const allDependencyIds = collectTransitiveVariableDependencies(
      dependencyIds,
      input.variables,
      selectedStepIds,
      missingRefs,
    );

    const variables: Record<string, FunnelVariable> = {};
    for (const variableId of sortedUnique(allDependencyIds)) {
      const variable = input.variables[variableId];
      if (variable) {
        variables[variableId] = deepClone(variable);
      }
    }

    return {
      variables,
      variableDependencyIds: sortedUnique(allDependencyIds),
      warnings: formatMissingRefWarning(missingRefs),
    };
  }

  planPasteVariables(input: V2PasteVariablePlanInput): V2PasteVariablePlan {
    const clipboardVariables = input.clipboardVariables || {};
    const sourceVariables = Object.values(clipboardVariables).filter((variable): variable is FunnelVariable =>
      Boolean(variable?.id),
    );

    const usedIds = new Set(Object.keys(input.targetVariables));
    const usedNames = new Set(
      Object.values(input.targetVariables)
        .map(variable => normalizeName(variable.name || variable.id))
        .filter(Boolean),
    );

    const variableIdMapping = new Map<string, string>();
    const sourceToNewIdentity = new Map<string, { id: string; name: string }>();

    for (const source of sourceVariables) {
      const sourceName = normalizeName(source.name || source.id);
      const hasConflict = usedIds.has(source.id) || (sourceName ? usedNames.has(sourceName) : false);
      const identity = hasConflict
        ? makeForkedIdentity(source, usedIds, usedNames)
        : { id: source.id, name: source.name || source.id };

      sourceToNewIdentity.set(source.id, identity);
      variableIdMapping.set(source.id, identity.id);
      variableIdMapping.set(getVariableReference(source), identity.id);
      if (source.name) {
        variableIdMapping.set(slugifyVariableRef(source.name), identity.id);
      }
      usedIds.add(identity.id);
      usedNames.add(normalizeName(identity.name));
    }

    const variablesToCreate: FunnelVariable[] = [];
    const variableRefMapping = new Map<string, string>();

    for (const source of sourceVariables) {
      const identity = sourceToNewIdentity.get(source.id)!;
      const sourceWithIdentity = {
        ...(source as VariableWithBindings),
        id: identity.id,
        name: identity.name,
      } as VariableWithBindings;
      const remapped = remapVariableScreenFields(
        sourceWithIdentity,
        input.stepIdMapping,
        variableIdMapping,
      );

      variablesToCreate.push(remapped as FunnelVariable);

      const targetRef = getVariableReference(remapped);
      variableRefMapping.set(source.id, targetRef);
      variableRefMapping.set(getVariableReference(source), targetRef);
      if (source.name) {
        variableRefMapping.set(slugifyVariableRef(source.name), targetRef);
      }
    }

    return {
      variablesToCreate,
      variableIdMapping,
      variableRefMapping,
      createdVariableIds: variablesToCreate.map(variable => variable.id),
      warnings: [],
    };
  }

  rewriteStepVariableReferences(input: V2StepVariableRewriteInput): FlowStepNode[] {
    // SPEC-030: the walk itself lives in v2-variable-utils so the builder-side
    // block-library instantiation shares the exact same rewrite semantics.
    return remapVariableReferencesInSteps(input.steps, {
      variableIdMapping: input.variableIdMapping,
      variableRefMapping: input.variableRefMapping,
    });
  }

  extractIdMappingForTests(steps: StepNode[], originalStepIds: string[]): Map<string, string> {
    const mapping = new Map<string, string>();
    steps.forEach((step, index) => {
      const originalId = originalStepIds[index];
      if (originalId && step.id) {
        mapping.set(originalId, step.id);
      }
    });
    return mapping;
  }
}

export const v2ClipboardVariablePortabilityService = new V2ClipboardVariablePortabilityService();
